import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { formatInTz } from '@root/lib/date-utils';
import TimeslotApiUtils from '@utils/api/timeslot';
import appUtils from '@utils/appUtils';
import MomentFormatter from '@utils/MomentFormatter';
import { useSecondaryModalStore } from '@root/stores';
import type {
  ShowTutorsOnSlotModalContainerProps,
  Tutor,
} from './show-tutors-on-slot-modal.types';

export const useShowTutorsOnSlotModalService = (
  props: ShowTutorsOnSlotModalContainerProps,
) => {
  const {
    hideModal,
    startTimestamp,
    endTimestamp,
    timeZone,
    ticketCount,
    subTicketCount,
  } = props;
  const { i18n } = useTranslation('common');
  const secondaryModalStore = useSecondaryModalStore();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [tutors, setTutors] = useState<Tutor[]>([]);

  useEffect(() => {
    const fetchTutors = async () => {
      setIsLoading(true);
      try {
        const data = await TimeslotApiUtils.fetchTutorsOnSlot(startTimestamp);
        setTutors(data.tutors || []);
      } catch (error) {
        console.error('Failed to fetch tutors:', error);
        setTutors([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTutors();
  }, [startTimestamp]);

  const handleBookTutor = useCallback(
    (tutor: Tutor) => {
      secondaryModalStore.show('booking', {
        secondary: true,
        tutor,
        tutorId: tutor.id,
        startTimestamp,
        endTimestamp,
        duration: 0,
        timeZone,
        ticketCount,
        subTicketCount,
        closeModal: true,
        type: 'Book',
      });
    },
    [
      secondaryModalStore,
      startTimestamp,
      endTimestamp,
      timeZone,
      ticketCount,
      subTicketCount,
    ],
  );

  const handleClose = useCallback(() => {
    if (!isSaving) {
      hideModal();
    }
  }, [isSaving, hideModal]);

  // Format date and time
  const currentLocale = appUtils.getLocaleFromLang(i18n.language);
  const formatter = new MomentFormatter(currentLocale, timeZone);
  const startFormat = formatter.lessonFormat(startTimestamp);
  const lessonDate = startFormat.formattedDate;
  const gmtOffset = formatInTz(startTimestamp, timeZone, 'xxx');
  const lessonTime = `${startFormat.formattedTime} (GMT${gmtOffset})`;

  return {
    isLoading,
    isSaving,
    tutors,
    lessonDate,
    lessonTime,
    message: props.message,
    messageType: props.messageType,
    onBookTutor: handleBookTutor,
    onClose: handleClose,
  };
};
